import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShieldCheck, ClipboardCheck, ArrowLeft, ArrowRight, Loader2, Send, ImagePlus, X,
    CheckCircle, AlertTriangle, MapPin, Tag, MessageSquare, ChevronRight, Calendar, Inbox, GraduationCap,
    BarChart3, ClipboardList, Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../utils/axiosConfig';
import Modal from './common/Modal';
import CalidadDashboard from './calidad/CalidadDashboard';
import CalidadReportes from './calidad/CalidadReportes';
import CalidadConexiones from './calidad/CalidadConexiones';
import DetalleReporte, { BadgeEstado, QueSeReporto } from './calidad/DetalleReporte';

const API = '/api/calidad/sura-reporta';
const MAX_FOTOS = 3;

// Textos de respaldo por si el catalogo del backend no trae alguna lista.
const CATALOGO_BASE = {
    significados: [
        { id: 'salud_bienestar', label: 'Proteger la salud y el bienestar de las familias que usan nuestros productos.' },
        { id: 'empaques_formulas', label: 'Entregar empaques impecables y fórmulas perfectas en cada lote.' },
        { id: 'compromiso_compartido', label: 'Un compromiso y una responsabilidad que compartimos todos los colaboradores.' },
        { id: 'todas', label: 'Todas las anteriores.' },
    ],
    habitos: [
        { id: 'orgullo_exhibicion', label: 'Siento orgullo y reviso cómo se ve exhibido.' },
        { id: 'detalles_sin_reportar', label: 'Me fijo en los detalles (empaque, tapa, aspecto), pero a veces no sabía a quién reportarlo.' },
        { id: 'no_observo', label: 'Casi no me detengo a observarlo, pero me gustaría empezar a hacerlo.' },
    ],
    lineas: [
        { id: 'personal', label: 'Cuidado Personal', ejemplos: 'champús, gel, etc.' },
        { id: 'bebe', label: 'Cuidado del Bebé', ejemplos: 'toallitas húmedas, cremas dermoprotectora, etc.' },
        { id: 'hogar', label: 'Cuidado del Hogar', ejemplos: 'desinfectantes, detergentes, etc.' },
    ],
    desviaciones: [
        { id: 'viscosidad', label: 'Viscosidad', descripcion: 'Producto muy líquido o separado.', requiere_foto: false },
        { id: 'consistencia', label: 'Consistencia', descripcion: 'Crema dura o endurecida.', requiere_foto: false },
        { id: 'tapa_cierre', label: 'Tapa o Cierre', descripcion: 'Tapa partida, rota, suelta o con fuga de contenido.', requiere_foto: true },
        { id: 'empaque_envase', label: 'Empaque / Envase', descripcion: 'Frasco deformado, abollado o sucio.', requiere_foto: true },
        { id: 'etiqueta', label: 'Etiqueta', descripcion: 'Etiqueta torcida, despegada, mal impresa o ilegible.', requiere_foto: true },
        { id: 'aroma_color', label: 'Aroma / Color', descripcion: 'Olor inusual o cambio de coloración.', requiere_foto: false },
        { id: 'otro', label: 'Otro', descripcion: '', requiere_foto: false },
    ],
    regiones: [],
    capsulas: [
        { id: 'si_totalmente', label: '¡Sí, totalmente! Me interesa aprender más.' },
        { id: 'si_linea', label: 'Sí, preferiblemente sobre la línea de productos que manejo.' },
        { id: 'no', label: 'Por ahora no.' },
    ],
};

// Acepta listas de textos o de objetos {id, label}.
const normalizar = (lista) => (lista || []).map(item => (
    typeof item === 'string' ? { id: item, label: item } : { ...item, id: item.id ?? item.valor ?? item.label, label: item.label ?? item.nombre ?? item.id }
));

const urlArchivo = (ruta) => (!ruta ? null : ruta.startsWith('/api') || ruta.startsWith('http') ? ruta : `/api${ruta}`);

const fechaLarga = (iso) => (iso ? new Date(iso).toLocaleDateString('es-VE', { year: 'numeric', month: 'long', day: 'numeric' }) : '');

// Opcion de seleccion unica o multiple con el estilo de tarjetas del sitio.
const Opcion = ({ seleccionada, onClick, multiple = false, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left text-sm transition-colors ${seleccionada ? 'border-sura-blue bg-blue-50 text-gray-900' : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
    >
        <span className={`mt-0.5 w-4 h-4 shrink-0 border-2 flex items-center justify-center ${multiple ? 'rounded' : 'rounded-full'} ${seleccionada ? 'border-sura-blue bg-sura-blue' : 'border-gray-300 bg-white'}`}>
            {seleccionada && <span className={`bg-white ${multiple ? 'w-1.5 h-1.5 rounded-sm' : 'w-1.5 h-1.5 rounded-full'}`} />}
        </span>
        <span className="flex-1">{children}</span>
    </button>
);

const Pregunta = ({ titulo, ayuda, children }) => (
    <div className="space-y-2.5">
        <div>
            <p className="text-sm font-semibold text-gray-900">{titulo}</p>
            {ayuda && <p className="text-xs text-gray-500 mt-0.5">{ayuda}</p>}
        </div>
        {children}
    </div>
);

// Hoy en YYYY-MM-DD local (toISOString es UTC y de noche corre al dia siguiente).
const hoyIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const DIAGNOSTICO_VACIO = {
    hayFalla: null, linea: '', desviaciones: [], desviacionOtro: '', fotos: [],
    // Identificacion del producto: el lote o, si no tiene lote visible, la fecha de fabricacion.
    lote: '', sinLote: false, fechaFabricacion: '',
    region: '', establecimiento: '', capsulas: '', sugerencia: '',
};

const SuraReportaView = () => {
    const navigate = useNavigate();
    const [cargando, setCargando] = useState(true);
    const [errorCarga, setErrorCarga] = useState('');
    const [estado, setEstado] = useState({ conexion_respondida: true, es_calidad: false });
    const [catalogoApi, setCatalogoApi] = useState({});
    const [pestana, setPestana] = useState('reportar');
    // Detalle de un reporte (seguimiento y conversacion con Calidad).
    const [detalleId, setDetalleId] = useState(null);
    const location = useLocation();

    // Seccion A (una sola vez)
    const [conexion, setConexion] = useState({ significado: '', habito: '' });
    const [guardandoConexion, setGuardandoConexion] = useState(false);
    // Seccion A en dos pasos (una pregunta por pantalla), para que no haga falta scroll.
    const [pasoConexion, setPasoConexion] = useState(0);

    // Diagnostico (B -> C), repetible
    const [asistente, setAsistente] = useState(null); // null | 'B' | 'C' | 'enviado'
    const [diag, setDiag] = useState(DIAGNOSTICO_VACIO);
    const [enviando, setEnviando] = useState(false);
    const [ultimoConFalla, setUltimoConFalla] = useState(false);

    // Mis reportes
    const [misDiagnosticos, setMisDiagnosticos] = useState([]);
    const [refrescoReportes, setRefrescoReportes] = useState(0);
    const [cargandoMios, setCargandoMios] = useState(false);
    const [previewImg, setPreviewImg] = useState(null);

    const catalogo = useMemo(() => {
        const c = {};
        Object.keys(CATALOGO_BASE).forEach(k => {
            const api = normalizar(catalogoApi[k]);
            c[k] = api.length ? api : CATALOGO_BASE[k];
        });
        return c;
    }, [catalogoApi]);

    const etiquetaDe = useCallback((lista, id) => (catalogo[lista].find(x => x.id === id)?.label) || id, [catalogo]);

    const cargar = useCallback(async () => {
        setCargando(true);
        setErrorCarga('');
        try {
            const [resEstado, resCat] = await Promise.all([
                axiosInstance.get(`${API}/estado`),
                axiosInstance.get(`${API}/catalogos`),
            ]);
            setEstado(resEstado.data || {});
            setCatalogoApi(resCat.data || {});
        } catch (error) {
            setErrorCarga(error.response?.data?.error || 'No se pudo cargar Sura Reporta. Intenta de nuevo.');
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const cargarMios = useCallback(async () => {
        setCargandoMios(true);
        try {
            const res = await axiosInstance.get(`${API}/mis-diagnosticos`);
            const data = res.data;
            setMisDiagnosticos(Array.isArray(data) ? data : (data?.diagnosticos || []));
        } catch (error) {
            toast.error(error.response?.data?.error || 'No se pudieron cargar tus reportes');
        } finally {
            setCargandoMios(false);
        }
    }, []);

    useEffect(() => {
        // En Reportar tambien: la tarjeta de inicio muestra cuantos diagnosticos lleva.
        if (pestana === 'mios' || pestana === 'reportar') cargarMios();
    }, [pestana, cargarMios]);

    // ---------------- Seccion A ----------------
    // El departamento no se pregunta: el backend guarda el que la persona tiene en CentroSura.
    const conexionCompleta = conexion.significado && conexion.habito;

    const guardarConexion = async () => {
        if (!conexionCompleta) {
            toast.error('Responde todas las preguntas para continuar.');
            return;
        }
        setGuardandoConexion(true);
        try {
            await axiosInstance.post(`${API}/conexion`, {
                significado: conexion.significado,
                habito: conexion.habito,
            });
            setEstado(prev => ({ ...prev, conexion_respondida: true }));
            toast.success('¡Gracias! Ya puedes usar Sura Reporta.');
        } catch (error) {
            toast.error(error.response?.data?.error || 'No se pudo guardar tu respuesta');
        } finally {
            setGuardandoConexion(false);
        }
    };

    // ---------------- Diagnostico ----------------
    const desviacionesMarcadas = catalogo.desviaciones.filter(d => diag.desviaciones.includes(d.id));
    const fotoObligatoria = desviacionesMarcadas.some(d => d.requiere_foto);

    const errorSeccionB = () => {
        if (diag.hayFalla === null) return 'Indica si observaste alguna desviación o falla.';
        if (diag.hayFalla === 'no') return null;
        if (!diag.linea) return 'Indica a qué línea pertenece el producto.';
        if (!diag.sinLote && !diag.lote.trim()) return 'Escribe el número de lote del producto.';
        if (diag.sinLote && !diag.fechaFabricacion) return 'Indica la fecha de fabricación del producto.';
        if (diag.sinLote && diag.fechaFabricacion > hoyIso()) return 'La fecha de fabricación no puede ser futura.';
        if (diag.desviaciones.length === 0) return 'Marca al menos un tipo de desviación.';
        if (diag.desviaciones.includes('otro') && !diag.desviacionOtro.trim()) return 'Describe brevemente la otra desviación.';
        if (fotoObligatoria && diag.fotos.length === 0) return 'Agrega al menos una foto del hallazgo.';
        if (!diag.region) return 'Indica el estado donde lo observaste.';
        if (!diag.establecimiento.trim()) return 'Indica la ubicación o el establecimiento.';
        return null;
    };

    const iniciar = () => {
        setDiag(DIAGNOSTICO_VACIO);
        setAsistente('B');
    };

    const alternarDesviacion = (id) => setDiag(prev => ({
        ...prev,
        desviaciones: prev.desviaciones.includes(id) ? prev.desviaciones.filter(x => x !== id) : [...prev.desviaciones, id],
        desviacionOtro: id === 'otro' && prev.desviaciones.includes('otro') ? '' : prev.desviacionOtro,
    }));

    const agregarFotos = (e) => {
        const archivos = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
        e.target.value = '';
        if (!archivos.length) return;
        setDiag(prev => {
            const disponibles = MAX_FOTOS - prev.fotos.length;
            if (archivos.length > disponibles) toast.error(`Máximo ${MAX_FOTOS} fotos por reporte.`);
            const nuevas = archivos.slice(0, Math.max(0, disponibles)).map(file => ({ file, preview: URL.createObjectURL(file) }));
            return { ...prev, fotos: [...prev.fotos, ...nuevas] };
        });
    };

    const quitarFoto = (idx) => setDiag(prev => {
        const foto = prev.fotos[idx];
        if (foto) URL.revokeObjectURL(foto.preview);
        return { ...prev, fotos: prev.fotos.filter((_, i) => i !== idx) };
    });

    const continuarAC = () => {
        const error = errorSeccionB();
        if (error) {
            toast.error(error);
            return;
        }
        setAsistente('C');
    };

    const enviar = async () => {
        const errorB = errorSeccionB();
        if (errorB) {
            toast.error(errorB);
            setAsistente('B');
            return;
        }
        if (!diag.capsulas) {
            toast.error('Responde si te gustaría recibir cápsulas de formación.');
            return;
        }
        setEnviando(true);
        try {
            const formData = new FormData();
            formData.append('hay_falla', diag.hayFalla);
            if (diag.hayFalla === 'si') {
                formData.append('linea', diag.linea);
                formData.append('desviaciones', JSON.stringify(diag.desviaciones));
                if (diag.sinLote) formData.append('fecha_fabricacion', diag.fechaFabricacion);
                else formData.append('lote', diag.lote.trim());
                if (diag.desviaciones.includes('otro')) formData.append('desviacion_otro', diag.desviacionOtro.trim());
                formData.append('region', diag.region);
                formData.append('establecimiento', diag.establecimiento.trim());
                diag.fotos.forEach(f => formData.append('fotos', f.file));
            }
            formData.append('capsulas', diag.capsulas);
            if (diag.sugerencia.trim()) formData.append('sugerencia', diag.sugerencia.trim());
            await axiosInstance.post(`${API}/diagnosticos`, formData);
            diag.fotos.forEach(f => URL.revokeObjectURL(f.preview));
            setUltimoConFalla(diag.hayFalla === 'si');
            setDiag(DIAGNOSTICO_VACIO);
            setAsistente('enviado');
        } catch (error) {
            toast.error(error.response?.data?.error || 'No se pudo enviar tu diagnóstico');
        } finally {
            setEnviando(false);
        }
    };

    // ---------------- Render ----------------
    const pestanas = [
        { id: 'reportar', label: 'Reportar', icon: ClipboardCheck },
        { id: 'mios', label: 'Mis reportes', icon: Inbox },
        ...(estado.es_calidad ? [
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'reportes', label: 'Reportes', icon: ClipboardList },
            { id: 'conexion', label: 'Resultados', icon: Users },
        ] : []),
    ];

    const renderPasos = () => (
        <div className="flex items-center gap-2 mb-5">
            {[{ id: 'B', label: 'Sura Reporta' }, { id: 'C', label: 'Formación y Cierre' }].map((p, i) => {
                const activo = asistente === p.id;
                const hecho = asistente === 'C' && p.id === 'B';
                return (
                    <React.Fragment key={p.id}>
                        {i > 0 && <div className={`flex-1 h-0.5 rounded ${hecho || activo ? 'bg-sura-blue' : 'bg-gray-200'}`} />}
                        <div className="flex items-center gap-2">
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${activo ? 'bg-sura-blue text-white' : hecho ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                                {hecho ? <CheckCircle size={14} /> : p.id}
                            </span>
                            <span className={`text-sm font-medium hidden sm:inline ${activo ? 'text-gray-900' : 'text-gray-500'}`}>Sección {p.id}: {p.label}</span>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );

    const renderSeccionB = () => (
        <motion.div key="B" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="space-y-6">
            <div>
                <h3 className="text-base font-bold text-gray-900">Sección B: Sura Reporta</h3>
                <p className="text-xs text-gray-500">Alerta de Anaquel y Producto</p>
            </div>

            <Pregunta titulo="¿Has observado recientemente alguna desviación o falla en nuestros productos en un anaquel, almacén o en tu hogar?">
                <div className="grid grid-cols-2 gap-2">
                    {[{ id: 'si', label: 'Sí' }, { id: 'no', label: 'No' }].map(op => (
                        <Opcion key={op.id} seleccionada={diag.hayFalla === op.id} onClick={() => setDiag(prev => ({ ...prev, hayFalla: op.id }))}>
                            {op.label}
                        </Opcion>
                    ))}
                </div>
            </Pregunta>

            {diag.hayFalla === 'si' && (
                <>
                    <Pregunta titulo="¿A qué línea pertenece el producto?">
                        <div className="space-y-2">
                            {catalogo.lineas.map(l => (
                                <Opcion key={l.id} seleccionada={diag.linea === l.id} onClick={() => setDiag(prev => ({ ...prev, linea: l.id }))}>
                                    {l.label}{l.ejemplos && <span className="text-gray-500"> ({l.ejemplos})</span>}
                                </Opcion>
                            ))}
                        </div>
                    </Pregunta>

                    <Pregunta
                        titulo={diag.sinLote ? 'Fecha de fabricación del producto' : 'Número de lote del producto'}
                        ayuda={diag.sinLote ? 'Búscala en el envase o la etiqueta.' : 'Lo encuentras en el envase o la etiqueta, junto a la fecha de vencimiento.'}
                    >
                        {diag.sinLote ? (
                            <input
                                type="date"
                                max={hoyIso()}
                                value={diag.fechaFabricacion}
                                onChange={e => setDiag(prev => ({ ...prev, fechaFabricacion: e.target.value }))}
                                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        ) : (
                            <input
                                type="text"
                                maxLength={60}
                                value={diag.lote}
                                onChange={e => setDiag(prev => ({ ...prev, lote: e.target.value }))}
                                placeholder="Ej: L2345A"
                                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        )}
                        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none w-fit">
                            <input
                                type="checkbox"
                                checked={diag.sinLote}
                                onChange={e => setDiag(prev => ({ ...prev, sinLote: e.target.checked, lote: '', fechaFabricacion: '' }))}
                                className="w-4 h-4 rounded border-gray-300 text-sura-blue focus:ring-blue-500"
                            />
                            El producto no tiene número de lote visible
                        </label>
                    </Pregunta>

                    <Pregunta titulo="¿Qué tipo de desviación detectaste en el anaquel o empaque?" ayuda="Selección múltiple">
                        <div className="space-y-2">
                            {catalogo.desviaciones.map(d => (
                                <Opcion key={d.id} multiple seleccionada={diag.desviaciones.includes(d.id)} onClick={() => alternarDesviacion(d.id)}>
                                    <span className="font-semibold">{d.label}{d.descripcion ? ':' : ''}</span> {d.descripcion}
                                </Opcion>
                            ))}
                        </div>
                        {diag.desviaciones.includes('otro') && (
                            <input
                                type="text"
                                maxLength={200}
                                value={diag.desviacionOtro}
                                onChange={e => setDiag(prev => ({ ...prev, desviacionOtro: e.target.value }))}
                                placeholder="Describe brevemente la desviación"
                                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        )}
                    </Pregunta>

                    <Pregunta
                        titulo={`Foto del Hallazgo${fotoObligatoria ? '' : ' (opcional)'}`}
                        ayuda={fotoObligatoria
                            ? 'Obligatoria para fallas de tapa, empaque o etiqueta. Hasta 3 fotos.'
                            : `Hasta ${MAX_FOTOS} fotos.`}
                    >
                        <div className="grid grid-cols-3 gap-2">
                            {diag.fotos.map((f, i) => (
                                <div key={f.preview} className="relative">
                                    <img
                                        src={f.preview}
                                        alt={`Foto ${i + 1}`}
                                        className="w-full h-24 rounded-xl object-cover border border-gray-200 cursor-zoom-in"
                                        onClick={() => setPreviewImg(f.preview)}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => quitarFoto(i)}
                                        className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full shadow"
                                        aria-label="Quitar foto"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                            {diag.fotos.length < MAX_FOTOS && (
                                <label className={`flex flex-col items-center justify-center gap-1 h-24 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${fotoObligatoria && diag.fotos.length === 0 ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-blue-300'}`}>
                                    <ImagePlus size={20} className="text-gray-400" />
                                    <span className="text-xs text-gray-500">Agregar foto</span>
                                    <input type="file" accept="image/*" multiple className="hidden" onChange={agregarFotos} />
                                </label>
                            )}
                        </div>
                    </Pregunta>

                    <Pregunta titulo="Ubicación o establecimiento donde lo observaste">
                        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-2">
                            <select
                                value={diag.region}
                                onChange={e => setDiag(prev => ({ ...prev, region: e.target.value }))}
                                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Estado</option>
                                {catalogo.regiones.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                            </select>
                            <input
                                type="text"
                                maxLength={200}
                                value={diag.establecimiento}
                                onChange={e => setDiag(prev => ({ ...prev, establecimiento: e.target.value }))}
                                placeholder="Ej: Farmacia / Supermercado X, Ciudad / Zona"
                                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </Pregunta>
                </>
            )}

            <div className="flex items-center justify-between gap-3 pt-2">
                <button
                    type="button"
                    onClick={() => setAsistente(null)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                    <ArrowLeft size={16} /> Cancelar
                </button>
                <button
                    type="button"
                    onClick={continuarAC}
                    disabled={diag.hayFalla === null}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-sura-blue hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Continuar <ArrowRight size={16} />
                </button>
            </div>
        </motion.div>
    );

    const renderSeccionC = () => (
        <motion.div key="C" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="space-y-6">
            <div>
                <h3 className="text-base font-bold text-gray-900">Sección C: Formación y Cierre</h3>
            </div>

            <Pregunta titulo="¿Te gustaría recibir cápsulas breves de formación (de 2 minutos) sobre atributos de calidad y buenas prácticas en nuestros productos?">
                <div className="space-y-2">
                    {catalogo.capsulas.map(c => (
                        <Opcion key={c.id} seleccionada={diag.capsulas === c.id} onClick={() => setDiag(prev => ({ ...prev, capsulas: c.id }))}>
                            {c.label}
                        </Opcion>
                    ))}
                </div>
            </Pregunta>

            <Pregunta titulo="¿Tienes alguna sugerencia para mejorar la calidad de nuestros productos o procesos?" ayuda="Opcional">
                <textarea
                    rows={4}
                    maxLength={1000}
                    value={diag.sugerencia}
                    onChange={e => setDiag(prev => ({ ...prev, sugerencia: e.target.value }))}
                    placeholder="Escribe tu sugerencia"
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
            </Pregunta>

            <div className="flex items-center justify-between gap-3 pt-2">
                <button
                    type="button"
                    onClick={() => setAsistente('B')}
                    disabled={enviando}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                    <ArrowLeft size={16} /> Atrás
                </button>
                <button
                    type="button"
                    onClick={enviar}
                    disabled={enviando || !diag.capsulas}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-sura-blue hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {enviando ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    Enviar
                </button>
            </div>
        </motion.div>
    );

    const renderReportar = () => {
        if (asistente === 'enviado') {
            return (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                        <CheckCircle size={28} className="text-emerald-500" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">{ultimoConFalla ? 'Reporte enviado' : 'Gracias por tu diagnóstico'}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                        {ultimoConFalla
                            ? 'El equipo de Aseguramiento de la Calidad recibió tu reporte.'
                            : 'Tu participación ayuda a cuidar la calidad de nuestros productos.'}
                    </p>
                    <button
                        type="button"
                        onClick={() => setAsistente(null)}
                        className="mt-6 px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-sura-blue hover:bg-blue-700 transition-colors"
                    >
                        Volver a Sura Reporta
                    </button>
                </motion.div>
            );
        }

        if (asistente === 'B' || asistente === 'C') {
            return (
                <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6">
                    {renderPasos()}
                    <AnimatePresence mode="wait">
                        {asistente === 'B' ? renderSeccionB() : renderSeccionC()}
                    </AnimatePresence>
                </div>
            );
        }

        return (
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-3xl mx-auto mt-6 bg-white rounded-3xl border border-gray-100 shadow-sm p-8 sm:p-10 text-center"
            >
                <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto">
                    <ClipboardCheck size={32} className="text-sura-blue" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mt-5">Diagnóstico y Reporte en Vivo</h2>
                <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                    Herramienta ágil de 3 minutos para capturar la percepción del colaborador y registrar desviaciones con fotos adjuntas.
                </p>

                {/* Lo que viene: la conexion ya esta hecha; quedan B y C */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-7 text-left">
                    {[
                        { icon: ShieldCheck, titulo: 'Conexión', texto: 'Ya respondida', hecho: true },
                        { icon: AlertTriangle, titulo: 'Sura Reporta', texto: '¿Viste alguna falla? Línea, tipo y fotos' },
                        { icon: GraduationCap, titulo: 'Formación y cierre', texto: 'Cápsulas y sugerencias' },
                    ].map(({ icon: Icon, titulo, texto, hecho }) => (
                        <div key={titulo} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${hecho ? 'bg-green-50' : 'bg-white border border-gray-200'}`}>
                                {hecho ? <CheckCircle size={16} className="text-green-600" /> : <Icon size={16} className="text-gray-400" />}
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900">{titulo}</p>
                                <p className="text-xs text-gray-500">{texto}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <button
                    type="button"
                    onClick={iniciar}
                    className="mt-8 inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl text-base font-medium text-white bg-sura-blue hover:bg-blue-700 transition-colors"
                >
                    Iniciar diagnóstico <ArrowRight size={18} />
                </button>

                <p className="text-xs text-gray-400 mt-4">
                    {misDiagnosticos.length === 0
                        ? 'Aún no has enviado diagnósticos.'
                        : `Has enviado ${misDiagnosticos.length} ${misDiagnosticos.length === 1 ? 'diagnóstico' : 'diagnósticos'}${misDiagnosticos[0]?.created_at ? ` · el último el ${new Date(misDiagnosticos[0].created_at).toLocaleDateString('es-VE', { day: 'numeric', month: 'long' })}` : ''}.`}
                </p>
            </motion.div>
        );
    };

    // Desde una notificacion: /sura-reporta?reporte=<id> abre ese reporte. Calidad lo
    // ve en su pestaña Reportes; quien lo hizo, en Mis reportes.
    useEffect(() => {
        if (cargando || !estado.conexion_respondida) return;
        const id = Number(new URLSearchParams(location.search).get('reporte'));
        if (!id) return;
        setPestana(estado.es_calidad ? 'reportes' : 'mios');
        setDetalleId(id);
        navigate(location.pathname, { replace: true });
    }, [cargando, estado.conexion_respondida, estado.es_calidad, location.search, location.pathname, navigate]);

    const renderMios = () => {
        if (cargandoMios) {
            return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-sura-blue" /></div>;
        }
        if (misDiagnosticos.length === 0) {
            return (
                <div className="py-16 text-center bg-white rounded-2xl border border-gray-100">
                    <Inbox size={32} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">Todavía no has enviado diagnósticos.</p>
                </div>
            );
        }
        return (
            <div className="space-y-3">
                {misDiagnosticos.map(d => {
                    const conFalla = d.hay_falla === true || d.hay_falla === 'si';
                    // Con falla es un reporte con seguimiento: al tocarlo se abre el detalle con la conversacion.
                    return (
                        <div
                            key={d.id}
                            onClick={conFalla ? () => setDetalleId(d.id) : undefined}
                            className={`bg-white rounded-2xl border border-gray-100 p-5 ${conFalla ? 'cursor-pointer hover:shadow-md hover:border-gray-200 transition-all' : ''}`}
                        >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="flex items-center gap-1.5 text-xs text-gray-500">
                                    <Calendar size={13} /> {fechaLarga(d.created_at || d.fecha)}
                                </span>
                                <div className="flex items-center gap-2">
                                    {d.critico && (
                                        <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600">
                                            <AlertTriangle size={12} /> Crítico
                                        </span>
                                    )}
                                    {conFalla
                                        ? <BadgeEstado estado={d.estado} label={d.estado_label} />
                                        : <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Sin falla</span>}
                                </div>
                            </div>

                            {conFalla ? (
                                <div className="mt-3 space-y-2 text-sm">
                                    <QueSeReporto linea={d.linea_label || etiquetaDe('lineas', d.linea)} desviaciones={d.desviaciones_detalle} />
                                    {(d.lote || d.fecha_fabricacion) && (
                                        <p className="flex items-center gap-1.5 text-xs text-gray-500">
                                            <Tag size={12} className="shrink-0" />
                                            {d.lote ? `Lote ${d.lote}` : `Sin lote · fabricado el ${new Date(d.fecha_fabricacion + 'T00:00:00').toLocaleDateString('es-VE')}`}
                                        </p>
                                    )}
                                    {(d.region || d.establecimiento) && (
                                        <p className="flex items-center gap-1.5 text-xs text-gray-500">
                                            <MapPin size={12} />
                                            {[d.region_label || (d.region && etiquetaDe('regiones', d.region)), d.establecimiento].filter(Boolean).join(' · ')}
                                        </p>
                                    )}
                                    {Array.isArray(d.fotos) && d.fotos.length > 0 && (
                                        <div className="flex gap-2 pt-1">
                                            {d.fotos.map((f, i) => {
                                                const ruta = typeof f === 'string' ? f : f?.url || f?.ruta;
                                                return (
                                                    <img
                                                        key={i}
                                                        src={urlArchivo(ruta)}
                                                        alt={`Foto ${i + 1}`}
                                                        className="w-16 h-16 rounded-lg object-cover border border-gray-100 cursor-zoom-in"
                                                        onClick={(e) => { e.stopPropagation(); setPreviewImg(urlArchivo(ruta)); }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="mt-3 text-sm text-gray-500">No reportaste desviaciones en este diagnóstico.</p>
                            )}

                            {(d.capsulas || d.sugerencia) && (
                                <div className="mt-3 pt-3 border-t border-gray-100 space-y-1 text-xs text-gray-500">
                                    {d.capsulas && (
                                        <p className="flex items-center gap-1.5"><GraduationCap size={12} /> {d.capsulas_label || etiquetaDe('capsulas', d.capsulas)}</p>
                                    )}
                                    {d.sugerencia && <p className="whitespace-pre-line">Sugerencia: {d.sugerencia}</p>}
                                </div>
                            )}

                            {conFalla && (
                                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-2 text-xs">
                                    <span className="flex items-center gap-1.5 text-gray-500">
                                        <MessageSquare size={13} />
                                        {(d.comentarios_count || 0) > 0
                                            ? `${d.comentarios_count} ${d.comentarios_count === 1 ? 'mensaje' : 'mensajes'}`
                                            : 'Sin mensajes'}
                                    </span>
                                    <span className="flex items-center gap-0.5 font-medium text-sura-blue">
                                        Ver seguimiento <ChevronRight size={14} />
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    if (cargando) {
        return <div className="flex justify-center py-24"><Loader2 size={28} className="animate-spin text-sura-blue" /></div>;
    }

    return (
        <div className="max-w-[960px] mx-auto animate-fadeIn pt-4 pb-8">
            {errorCarga && (
                <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-xl text-sm">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <span className="flex-1">{errorCarga}</span>
                    <button type="button" onClick={cargar} className="font-medium underline">Reintentar</button>
                </div>
            )}

            <div className="mb-8">
                <h1 className="text-[1.625rem] font-bold text-slate-900 leading-tight">Sura Reporta</h1>
                <p className="text-sm text-gray-500 mt-1">Calidad</p>
            </div>

            {estado.conexion_respondida && !errorCarga && (
                <>
                    <div className="flex flex-wrap gap-1 mb-6 border-b-[1.5px] border-slate-100">
                        {pestanas.map(({ id, label, icon: Icon }) => (
                            <button
                                key={id}
                                onClick={() => setPestana(id)}
                                className={`flex items-center gap-1.5 px-[1.125rem] py-3 -mb-[1.5px] border-b-2 text-sm whitespace-nowrap transition-colors ${pestana === id ? 'border-sura-blue text-sura-blue font-semibold' : 'border-transparent text-slate-400 font-medium hover:text-slate-600'}`}
                            >
                                <Icon size={16} /> {label}
                            </button>
                        ))}
                    </div>

                    {pestana === 'reportar' && renderReportar()}
                    {pestana === 'mios' && renderMios()}
                    {estado.es_calidad && pestana === 'dashboard' && <CalidadDashboard />}
                    {estado.es_calidad && pestana === 'reportes' && <CalidadReportes refresco={refrescoReportes} />}
                    {estado.es_calidad && pestana === 'conexion' && <CalidadConexiones />}
                </>
            )}

            {/* Seccion A: obligatoria y de una sola vez, antes de entrar al modulo */}
            <Modal isOpen={!cargando && !errorCarga && !estado.conexion_respondida} onClose={() => {}} closeOnEscape={false} size="md">
                <Modal.Header showCloseButton={false}>
                    <div className="flex items-center gap-2">
                        <ShieldCheck size={20} className="text-sura-blue" />
                        <span>Conexión con la Calidad</span>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {(() => {
                        const preguntas = [
                            {
                                titulo: 'Para ti, ¿qué representa la Calidad en Sura de Venezuela?',
                                opciones: catalogo.significados,
                                valor: conexion.significado,
                                elegir: (id) => setConexion(prev => ({ ...prev, significado: id })),
                            },
                            {
                                titulo: 'Cuando ves un producto de Sura en el anaquel de un supermercado o farmacia, ¿qué suele ocurrir?',
                                opciones: catalogo.habitos,
                                valor: conexion.habito,
                                elegir: (id) => setConexion(prev => ({ ...prev, habito: id })),
                            },
                        ];
                        const actual = preguntas[pasoConexion];
                        return (
                            <div className="space-y-5">
                                <div className="space-y-2.5">
                                    <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
                                        <span>Solo se responde una vez · Pregunta {pasoConexion + 1} de {preguntas.length}</span>
                                        {estado.departamento && (
                                            <span className="px-2 py-0.5 bg-gray-100 rounded-full text-gray-600 truncate max-w-[45%]">{estado.departamento}</span>
                                        )}
                                    </div>
                                    <div className="flex gap-1.5">
                                        {preguntas.map((_, i) => (
                                            <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${i <= pasoConexion ? 'bg-sura-blue' : 'bg-gray-200'}`} />
                                        ))}
                                    </div>
                                </div>

                                <AnimatePresence mode="wait" initial={false}>
                                    <motion.div
                                        key={pasoConexion}
                                        initial={{ opacity: 0, x: 16 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -16 }}
                                        transition={{ duration: 0.18, ease: 'easeOut' }}
                                        className="space-y-3"
                                    >
                                        <p className="text-base font-semibold text-gray-900 leading-snug">{actual.titulo}</p>
                                        <div className="space-y-2">
                                            {actual.opciones.map(o => (
                                                <Opcion key={o.id} seleccionada={actual.valor === o.id} onClick={() => actual.elegir(o.id)}>
                                                    {o.label}
                                                </Opcion>
                                            ))}
                                        </div>
                                    </motion.div>
                                </AnimatePresence>
                            </div>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex items-center gap-3 w-full">
                        <button
                            type="button"
                            onClick={() => (pasoConexion === 0 ? navigate('/inicio') : setPasoConexion(0))}
                            className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                        >
                            {pasoConexion > 0 && <ArrowLeft size={16} />}
                            {pasoConexion === 0 ? 'Volver' : 'Atrás'}
                        </button>
                        {pasoConexion === 0 ? (
                            <button
                                type="button"
                                onClick={() => setPasoConexion(1)}
                                disabled={!conexion.significado}
                                className="flex-1 py-2.5 bg-sura-blue text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                Siguiente
                                <ArrowRight size={16} />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={guardarConexion}
                                disabled={!conexionCompleta || guardandoConexion}
                                className="flex-1 py-2.5 bg-sura-blue text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {guardandoConexion ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                                Continuar
                            </button>
                        )}
                    </div>
                </Modal.Footer>
            </Modal>

            {detalleId && (
                <DetalleReporte
                    diagnosticoId={detalleId}
                    onClose={() => setDetalleId(null)}
                    onCambio={() => { if (pestana === 'mios') cargarMios(); else setRefrescoReportes(n => n + 1); }}
                />
            )}

            {/* Visor de fotos */}
            {previewImg && (
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4 cursor-zoom-out"
                    onClick={() => setPreviewImg(null)}
                >
                    <img src={previewImg} alt="Foto del hallazgo" className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl" />
                </div>
            )}
        </div>
    );
};

export default SuraReportaView;
